export type FormatTimestamp = {
  /** Time of day: 2:32 PM (en) or 14:32 (pl). Empty for an invalid date. */
  time: (date: Date) => string;
  /**
   * Time of day without the day period: 2:32 (en) or 14:32 (pl). For a line
   * under one that already says the whole time.
   */
  clock: (date: Date) => string;
  /**
   * Day with its weekday: Monday, October 5. The year only when it is not the
   * year of `now`. Empty for an invalid date.
   */
  day: (date: Date, now: Date) => string;
  /**
   * Day with the weekday and month abbreviated: Mon, Oct 5. The year only when
   * it is not the year of `now`. Empty for an invalid date.
   */
  shortDay: (date: Date, now: Date) => string;
  /** Date and time in full, for a tooltip. Empty for an invalid date. */
  dateTime: (date: Date) => string;
};

const isValid = (date: Date) => !Number.isNaN(date.getTime());

/** Locale-aware timestamps (time of day, day, full date and time) for a thread of messages. */
export function createFormatTimestamp(locale: string): FormatTimestamp {
  const intlLocale = locale === 'en' ? 'en-US' : locale;
  const time = new Intl.DateTimeFormat(intlLocale, {
    hour: 'numeric',
    minute: '2-digit'
  });
  const dayThisYear = new Intl.DateTimeFormat(intlLocale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
  const dayWithYear = new Intl.DateTimeFormat(intlLocale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const shortDayThisYear = new Intl.DateTimeFormat(intlLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  const shortDayWithYear = new Intl.DateTimeFormat(intlLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const dateTime = new Intl.DateTimeFormat(intlLocale, {
    dateStyle: 'full',
    timeStyle: 'short'
  });

  return {
    time: date => (isValid(date) ? time.format(date) : ''),
    clock: date =>
      isValid(date)
        ? time
            .formatToParts(date)
            .filter(part => part.type !== 'dayPeriod')
            .map(part => part.value)
            .join('')
            .trim()
        : '',
    day: (date, now) => {
      if (!isValid(date)) {
        return '';
      }
      const sameYear = date.getFullYear() === now.getFullYear();
      return (sameYear ? dayThisYear : dayWithYear).format(date);
    },
    shortDay: (date, now) => {
      if (!isValid(date)) {
        return '';
      }
      const sameYear = date.getFullYear() === now.getFullYear();
      return (sameYear ? shortDayThisYear : shortDayWithYear).format(date);
    },
    dateTime: date => (isValid(date) ? dateTime.format(date) : '')
  };
}
